// Versioned, bounded office edits. Pure: the Office and browser derive the SAME map; BASE is never mutated.
// Palette artwork is copied from existing atlas cells, never uploaded or supplied by a client.
import { collider } from './collide.js';
import { ROOM_DEFAULT_NAMES, PROP_SEAT_NAMES } from './map-names.js';
export const MAP_SCHEMA = 1, MAX_OBJECTS = 160, MAX_TILES = 4000;
export const MAX_ROOMS = 20;
/** How far past the base map's edge an edit may build (tiles, each side): artin 11-02 「然后就可以无限画布了，只要他岛越造越大
 *  就可以」. The map grows with what is built out there (applyOverlay: its grids reach the farthest edit, x0 / y0 may go
 *  below 0), and the open sea round it (SEA_MARGIN) follows. */
export const GROW = 48;
/** Inside the base map's own grid. */
export const inBase = (base, x, y) => x >= 0 && y >= 0 && x < base.width && y < base.height;
/** Inside where an edit may be: the base map and GROW tiles round it. */
export const inReach = (base, x, y) => int(x) && int(y) && x >= -GROW && y >= -GROW && x < base.width + GROW && y < base.height + GROW;
/** A number for cell (x, y) of the reach (keys of sets and maps of cells; negative x and y too). */
const cellKey = (base, x, y) => (y + GROW) * (base.width + 2 * GROW) + x + GROW;
/** The base map's walk / sea grid at (x, y); past its edge the open sea. */
const baseSolid = (base, x, y) => (inBase(base, x, y) ? base.solid[y][x] : '1');
// (the editor words each terrain, prop and group by id: mapEditor.terrain.<id>, mapEditor.prop.<id>, mapEditor.group.<id>)
export const TERRAINS = [
  { id: 'deck', x: 30, y: 23, water: '0', solid: '0' }, // 木地板
  { id: 'path', x: 31, y: 20, water: '0', solid: '0' }, // 木栈道
  { id: 'sand', x: 101, y: 19, water: '0', solid: '0' },
  { id: 'grass', x: 96, y: 12, water: '0', solid: '0' },
  { id: 'water', x: 80, y: 30, water: '1', solid: '1' },
  { id: 'wall', x: 10, y: 10, water: '0', solid: '1' }, // 木墙
];
// Source rectangles in map.tmj, including the furniture's seats and depth-sorted fronts. Extend this catalog for
// new interactive props (e.g. whiteboards); never accept client-supplied rectangles, seats, collision or asset URLs.
// group: what it is for. 'office' (办公家具): desks, seats, tables and decoration, what members arrange. 'game' (游戏设施):
// the game tables and huts. Those are not in this catalog: they are the base map's (base.tables, the huts in
// base.zones), never an overlay object, and protectedTile keeps every edit (a tile, an object, a room, a drag, the
// eraser) off them and their approaches, so arranging the office can't break a game. A game prop added here later
// must keep that: its own group, and the same protection for what it stands on.
// legacy: no longer offered, still read: the 2-tile desk of the first map (the desks are 3 tiles wide since; an old
// 'desk' becomes a 'workdesk' where it fits, normalizeOverlay).
export const PROP_GROUPS = [{ id: 'office' }, { id: 'game' }];
export const PROPS = [
  { id: 'workdesk', x: 9, y: 25, w: 3, h: 3, group: 'office' }, // 电脑桌椅
  { id: 'meeting', x: 55, y: 24, w: 6, h: 5, group: 'office' }, // 会议圆桌
  { id: 'bench', x: 12, y: 44, w: 2, h: 2, group: 'office' },
  { id: 'cushion', x: 64, y: 33, w: 1, h: 1, group: 'office' },
  { id: 'plant', x: 8, y: 25, w: 1, h: 2, group: 'office' },
  { id: 'barrel', x: 7, y: 5, w: 1, h: 2, group: 'office' },
  { id: 'lamp', x: 27, y: 17, w: 1, h: 2, group: 'office' },
  { id: 'flowers', x: 96, y: 13, w: 1, h: 1, group: 'office' },
  { id: 'desk', x: 10, y: 25, w: 2, h: 3, group: 'office', legacy: true },
];
/** The palette on this base map: TERRAINS and PROPS as they are on the office raft (map.tmj), or, on a map with a
 *  palette property (the 活动舱, play.tmj; deck/map.js palette), only the ones it has, copied from its own cells. Same ids,
 *  names, sizes and groups everywhere; only where the art is copied from changes. */
const palettes = new WeakMap();
function paletteOf(base) {
  let v = palettes.get(base); if (v) return v;
  const P = base?.palette;
  const pick = (list, at) => (P ? list.flatMap((t) => { const c = at?.[t.id]; return Array.isArray(c) && int(c[0]) && int(c[1]) ? [{ ...t, x: c[0], y: c[1] }] : []; }) : list);
  v = { terrains: pick(TERRAINS, P?.terrains), props: pick(PROPS, P?.props) };
  if (base && typeof base === 'object') palettes.set(base, v);
  return v;
}
export const terrainsOf = (base) => paletteOf(base).terrains;
export const propsOf = (base) => paletteOf(base).props;
/** Where an old 2-tile 'desk' at (x, y) may go as a 3-tile 'workdesk', best first: one tile left (its seat stays
 *  about where it was), then where it is; else it stays a 'desk' (always fits where it was). */
const UPGRADE = { desk: (o) => [{ ...o, kind: 'workdesk', x: o.x - 1 }, { ...o, kind: 'workdesk' }] };
/** The overlay as it is STORED and SENT (the Office's rows, welcome, map_changed): every 'workdesk' written as the old
 *  2-tile 'desk' one tile to its right, inside its own footprint. UPGRADE reads that back to the same workdesk (its
 *  first try, x - 1), and a reader from before 'workdesk' (a rolled-back release, a page not reloaded since) reads a
 *  desk it knows instead of dropping one it doesn't (and then saving the map without it). Only the live map, in the
 *  Office's memory and on the page, has workdesks. Review of #135 (Gogo, cross). */
export const storedOverlay = (overlay) => { const { removals, ...rest } = overlay; return { ...rest, ...(removals?.length ? { removals } : {}),
  objects: overlay.objects.map((o) => (o.kind === 'workdesk' ? { id: o.id, kind: 'desk', x: o.x + 1, y: o.y } : o)) }; };
// (removals: written only when there are some, so a map without them is sent and kept exactly as before)
export const emptyOverlay = () => ({ schema: MAP_SCHEMA, revision: 0, tiles: [], objects: [], rooms: [], removals: [] });
const inside = (r, x, y) => x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;
const int = (n) => Number.isSafeInteger(n);
export function propAt(overlay, x, y) { return overlay.objects.find((o) => { const p = PROPS.find((p) => p.id === o.kind); return p && inside({ ...p, x: o.x, y: o.y }, x, y); }); }
export const roomAt = (overlay, x, y) => (overlay.rooms ?? []).find(r => inside(r,x,y));
const doorXs = r => [r.x+Math.floor(r.w/2)-1,r.x+Math.floor(r.w/2)];
const roomWall = (r,x,y) => (x===r.x||x===r.x+r.w-1||y===r.y||y===r.y+r.h-1) && !(y===r.y+r.h-1&&doorXs(r).includes(x));
/** Game facilities: the 五子棋 tables and the 牌桌 (with their stools and onlookers' ring) and the game huts (base.zones that aren't
 *  rooms: 你画我猜, 谁是卧底 · 狼人杀, OtterBonk) with a tile round them. Every edit stays off them (protectedTile). */
export function gameArea(base, x, y) {
  if ((base.tables ?? []).some((p) => Math.abs(x - p.x) <= 3 && Math.abs(y - p.y) <= 3)) return true;
  if ((base.cardTables ?? []).some((p) => Math.abs(x - p.x) <= 4 && Math.abs(y - p.y) <= 3)) return true; // 牌桌, its seats and a tile round
  return base.zones.some((z) => !String(z.id).startsWith('holt-') && inside({ x: z.x - 1, y: z.y - 1, w: z.w + 2, h: z.h + 2 }, x, y));
}
/** What stays as the base map has it, whatever the edit: the buildings (rooms and game huts, a tile round them), the
 *  game tables and their rings, the spawn and gather spots, the 码头 / 跳板 and where people land from it, and the
 *  seats (desks, benches, meeting tables, cushions: owners and voice circles hang on them). Also off the map. */
export function fixedTile(base, x, y) {
  if (!inReach(base, x, y)) return true;
  if (!inBase(base, x, y)) return false; // the open sea past the edge: anyone may build there
  if (gameArea(base, x, y)) return true;
  if (base.zones.some((z) => inside({ x: z.x - 1, y: z.y - 1, w: z.w + 2, h: z.h + 2 }, x, y))) return true;
  if ([base.spawn, base.gather].some((p) => Math.abs(x - p.x) <= 3 && Math.abs(y - p.y) <= 3)) return true;
  // the 码头 / 跳板 to the other boat, a tile round it, and where people coming from there land
  if ((base.portals ?? []).some((q) => inside({ x: q.x - 1, y: q.y - 1, w: q.w + 2, h: q.h + 2 }, x, y) || (Math.abs(x - Math.floor(q.arrive.x + 0.5)) <= 1 && Math.abs(y - Math.floor(q.arrive.y + 0.8)) <= 1))) return true;
  return (base.seats ?? []).some((s) => Math.abs(x + .5 - s.fx) < 1.5 && Math.abs(y + .5 - s.fy) < 1.5);
}
/** The base map's own things that stand up (its fronts: crates, barrels, life rings, bollards, lamps, planters, palms,
 *  rocks, buoys, the desks, the mast ...), each with a stable id from where it is drawn (`f-<x>-<y>-<w>-<h>`, px), the
 *  cells it covers, and whether an edit may take it away (removable: none of its cells is a fixedTile). A removed one
 *  (overlay.removals: its id) is not drawn, and its cells get the ground next to them (fill: cell -> the art of the
 *  nearest free cell of the same kind, the rim's continuing the rim); then they can be painted, built on, or have
 *  props or rooms like any other cell. Where one can walk or swim doesn't change (the decorations never did); the big ones
 *  (palms, masts, the carousel, desks, meeting tables) stay. An id
 *  the base map no longer has (it was redrawn) is dropped on restore: the thing it named isn't there to hide. */
const objectsCache = new WeakMap(), SMALL = 6;
export function baseObjects(base) {
  let v = objectsCache.get(base); if (v) return v;
  const W = base.width, H = base.height, T = base.tile, list = [], byCell = new Map(), byId = new Map();
  // the main deck: the biggest piece of land (the raft; the 活动舱's deck). Things on the islands stay: their sand, grass
  // and shore curves are drawn across cells, and no neighbouring cell's art puts that back (review of this change)
  const piece = new Int32Array(W * H).fill(-1), sizes = [];
  for (let k = 0; k < W * H; k++) if (piece[k] < 0 && base.water[Math.floor(k / W)][k % W] !== '1') {
    const n = sizes.length, q = [k]; piece[k] = n; let size = 0;
    while (q.length) { const c = q.pop(), x = c % W, y = Math.floor(c / W); size++;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const nx = x + dx, ny = y + dy, nk = ny * W + nx;
        if (nx >= 0 && ny >= 0 && nx < W && ny < H && piece[nk] < 0 && base.water[ny][nx] !== '1') { piece[nk] = n; q.push(nk); } } }
    sizes.push(size);
  }
  const main = sizes.indexOf(Math.max(...sizes));
  const onMainOrSea = (k) => base.water[Math.floor(k / W)][k % W] === '1' || piece[k] === main;
  for (const f of base.fronts?.list ?? []) {
    let id = `f-${f.x}-${f.y}-${f.w}-${f.h}`; for (let n = 2; byId.has(id); n++) id = `f-${f.x}-${f.y}-${f.w}-${f.h}-${n}`;
    const cells = [];
    for (let y = Math.max(0, Math.floor(f.y / T)); y < Math.min(H, Math.ceil((f.y + f.h) / T)); y++)
      for (let x = Math.max(0, Math.floor(f.x / T)); x < Math.min(W, Math.ceil((f.x + f.w) / T)); x++) cells.push(y * W + x);
    // removable: small (a crate, a barrel, a ring, a lamp: up to SMALL cells, so the ground put back reads as ground; a
    // palm's crown or a mast spans too much of what is under it), on the main deck or at sea (not an island, nor a wall
    // or the solid picture of a boat: taking it away must not leave an invisible wall), and on no fixedTile
    const solidLand = (k) => base.solid[Math.floor(k / W)][k % W] === '1' && base.water[Math.floor(k / W)][k % W] !== '1';
    const o = { id, front: f, cells, removable: cells.length <= SMALL && cells.every(onMainOrSea) && !cells.some(solidLand) && cells.every((k) => !fixedTile(base, k % W, Math.floor(k / W))) };
    list.push(o); byId.set(id, o);
    for (const k of cells) (byCell.get(k) ?? byCell.set(k, []).get(k)).push(o);
  }
  // and none that shares a cell with one that stays (the ground put back there would cut into that one's picture)
  for (let changed = true; changed;) { changed = false;
    for (const o of list) if (o.removable && o.cells.some((k) => byCell.get(k).some((q) => !q.removable))) { o.removable = false; changed = true; } }
  // the ground under each removable thing: the art of a cell nearby (up to 3 away: farther is another place) that
  // nothing stands on, of the same kind (sea or land, walkable or not, and the same sides next to the other of sea or
  // land: a straight rim takes a straight rim, never a corner): the nearest, then the commonest.
  const fill = new Map(), free = (x, y) => x >= 0 && y >= 0 && x < W && y < H && !byCell.has(y * W + x);
  const wet = (x, y) => base.water[y][x];
  const sides = (x, y) => [[1, 0], [-1, 0], [0, 1], [0, -1]].reduce((m, [dx, dy], i) => m | ((base.water[y + dy]?.[x + dx] ?? wet(x, y)) !== wet(x, y) ? 1 << i : 0), 0);
  const kindOf = (x, y) => `${wet(x, y)}${base.solid[y][x]}${sides(x, y)}`;
  const often = new Map(); // kind -> art -> how many cells
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (free(x, y)) {
    const k = kindOf(x, y), m = often.get(k) ?? often.set(k, new Map()).get(k), g = base.art.data[y * W + x]; m.set(g, (m.get(g) ?? 0) + 1); }
  for (const o of list) if (o.removable) for (const k of o.cells) {
    if (fill.has(k)) continue;
    const x = k % W, y = Math.floor(k / W), kind = kindOf(x, y), counts = often.get(kind);
    // the nearest (the planks round a lamp, not the boardwalk 3 away), then the one most of them have, then the one
    // the map has most often (plain rim over a post)
    // (each way out to the first cell of another kind: a wall ends it, so a room's floor behind it doesn't count)
    const near = new Map();
    for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) for (let d = 1; d <= 3; d++) {
      const qx = x + dx * d, qy = y + dy * d;
      if (qx < 0 || qy < 0 || qx >= W || qy >= H || kindOf(qx, qy) !== kind) break;
      if (!free(qx, qy)) continue;
      const g = base.art.data[qy * W + qx], o = near.get(g); near.set(g, { d: Math.min(o?.d ?? d, d), n: (o?.n ?? 0) + 1 });
    }
    let best = null, score = -Infinity;
    for (const [g, { d, n }] of near) { const sc = -d * 1e9 + n * 1e6 + (counts?.get(g) ?? 0); if (sc > score) { score = sc; best = g; } }
    // nothing of its kind near: the commonest art of its kind anywhere, else as it was
    fill.set(k, best ?? (counts ? [...counts].sort((a, b) => b[1] - a[1])[0][0] : base.art.data[k]));
  }
  v = { list, byCell, byId, fill };
  objectsCache.set(base, v);
  return v;
}
const NONE = new Set();
/** Can an edit change this cell? Not a fixedTile, and nothing of the base map stands there, unless it was removed
 *  (`removed`: the overlay's removals, a Set of ids). */
export function protectedTile(base, x, y, removed = NONE) { return protectedCell(base, x, y, removed); }
function protectedCell(base, x, y, removed) {
  if (fixedTile(base, x, y)) return true;
  return inBase(base, x, y) && (baseObjects(base).byCell.get(y * base.width + x) ?? []).some((o) => !removed.has(o.id));
}
/** The removable things of the base map standing on cell (x, y) that aren't removed yet (an edit there takes them away). */
export const baseObjectsAt = (base, x, y, removed = NONE) =>
  (int(x) && int(y) && x >= 0 && y >= 0 && x < base.width && y < base.height ? baseObjects(base).byCell.get(y * base.width + x) ?? [] : [])
    .filter((o) => o.removable && !removed.has(o.id));
/** Strict for new edits. Restore only accepts trusted stored/server snapshots: keep legacy door layouts,
 *  skip a damaged room (and dependent invalid props) or a tile the base map has since taken (a protected cell), and
 *  report recovery without rewriting stored rows. Objects only carry {id, kind, x, y} (no group or other field: their
 *  group is their prop's), so old snapshots read as they are; an old 'desk' is upgraded (UPGRADE; onUpgrade). */
// A refused edit throws an Error with a code (the page words it: mapEditor.err.<code>) and an English message (for logs
// and pages from before the code): E_FORMAT, E_TOO_BIG, E_TILE, E_TILE_TWICE, E_ROOMS, E_ROOMS_MAX, E_ROOM_SIZE,
// E_ROOM_FIXED, E_ROOM_OVERLAP, E_ROOM_DOOR, E_ROOM_DOOR_IN, E_ROOM_BLOCKS_DOOR, E_OBJECT, E_OBJECT_FIXED,
// E_OBJECT_OVERLAP, E_OBJECT_GROUND, E_OBJECT_DOOR, E_BUILTIN.
/** A room's name as stored: control characters out, trimmed, at most 32 characters. */
const roomName = (v) => (typeof v === 'string' ? [...v.replace(/[\p{Cc}\p{Cf}\p{Cs}]/gu, '').trim()].slice(0, 32).join('') : '');
export function normalizeOverlay(base, raw, { restore = false, onRecover = () => {}, onUpgrade = () => {} } = {}) {
  const fail = (code, s) => { throw Object.assign(new Error(s), { code }); };
  const recover = (run) => { try { return run(); } catch (e) { if (!restore) throw e; onRecover(); return null; } };
  if (!raw || raw.schema !== MAP_SCHEMA || !int(raw.revision) || raw.revision < 0 || !Array.isArray(raw.tiles) || !Array.isArray(raw.objects)) fail('E_FORMAT', 'malformed map');
  if (raw.tiles.length > MAX_TILES || raw.objects.length > MAX_OBJECTS) fail('E_TOO_BIG', 'too many tiles or objects');
  const coords = new Set(), ids = new Set(), TER = terrainsOf(base), PRO = propsOf(base);
  // built-in things taken away (baseObjects): ids of removable ones; on restore an id this base map doesn't have is skipped
  if (raw.removals !== undefined && !Array.isArray(raw.removals)) throw Object.assign(new Error('removals must be a list'), { code: 'E_BUILTIN' });
  const OBJ = baseObjects(base), removed = new Set();
  for (const id of raw.removals ?? []) {
    if (typeof id !== 'string' || !OBJ.byId.get(id)?.removable) { if (restore) { onRecover(); continue; } throw Object.assign(new Error('a built-in thing this map does not have, or that must stay'), { code: 'E_BUILTIN' }); }
    removed.add(id);
  }
  const removals = [...removed].sort();
  const protectedTile = (b, x, y) => protectedCell(b, x, y, removed);
  const tiles = raw.tiles.map((t) => {
    if (!t || !int(t.x) || !int(t.y) || !TER.some((p) => p.id === t.kind)) fail('E_TILE', 'a tile outside the editable area');
    if (protectedTile(base, t.x, t.y)) return recover(() => fail('E_TILE', 'a tile outside the editable area'));
    const key = cellKey(base, t.x, t.y); if (coords.has(key)) fail('E_TILE_TWICE', 'the same tile twice'); coords.add(key);
    return { x: t.x, y: t.y, kind: t.kind };
  }).filter(Boolean).sort((a, b) => a.y - b.y || a.x - b.x);
  const terrainAt = new Map(tiles.map((t) => [cellKey(base, t.x, t.y), TER.find((p) => p.id === t.kind)]));
  if (raw.rooms !== undefined && !Array.isArray(raw.rooms)) fail('E_ROOMS', 'malformed rooms');
  if ((raw.rooms?.length ?? 0) > MAX_ROOMS) fail('E_ROOMS_MAX', 'too many rooms');
  const roomIds = new Set(), roomCells = new Map(), rooms = [];
  for (const r of raw.rooms ?? []) {
    const room = recover(() => {
      if (!r || typeof r.id !== 'string' || !/^[a-zA-Z0-9-]{1,48}$/.test(r.id) || roomIds.has(r.id) || !int(r.x) || !int(r.y) || !int(r.w) || !int(r.h) || r.w < 6 || r.h < 5 || r.w > 30 || r.h > 24 || !['meeting','quiet'].includes(r.kind)) fail('E_ROOM_SIZE', 'a room must be 6-30 tiles wide and 5-24 tall');
      // name: as typed (no name: its kind's default, map-names.js); nameEn: its English name, if its editor gave one
      const name = roomName(r.name), nameEn = roomName(r.nameEn);
      const next = { id:r.id, name:name || ROOM_DEFAULT_NAMES[r.kind]['zh-CN'], ...(nameEn ? { nameEn } : {}), kind:r.kind, x:r.x, y:r.y, w:r.w, h:r.h };
      for (let y = r.y; y < r.y+r.h; y++) for (let x = r.x; x < r.x+r.w; x++) {
        if (protectedTile(base,x,y)) fail('E_ROOM_FIXED', 'a room over a building, a facility or the spawn spot');
        if (roomCells.has(cellKey(base,x,y))) fail('E_ROOM_OVERLAP', 'rooms overlap');
      }
      for (const x of doorXs(r)) {
        const y = r.y+r.h, key = cellKey(base,x,y);
        if (!inReach(base, x, y) || roomCells.has(key) || (terrainAt.get(key)?.solid ?? baseSolid(base, x, y)) !== '0') fail('E_ROOM_DOOR', 'a room door needs two free tiles outside');
        // The room lays its own floor over the base, but explicitly painted interior terrain survives.
        if (!restore && (terrainAt.get(cellKey(base, x, y-2))?.solid ?? '0') !== '0') fail('E_ROOM_DOOR_IN', 'a room door needs two free tiles inside');
      }
      // Adding a later room must not cover an earlier room's outside approach.
      for (const old of rooms) for (const x of doorXs(old))
        if (inside(r,x,old.y+old.h)) fail('E_ROOM_BLOCKS_DOOR', 'a room in front of another room\'s door');
      return next;
    });
    if (!room) continue;
    roomIds.add(room.id); rooms.push(room);
    for (let y = room.y; y < room.y+room.h; y++) for (let x = room.x; x < room.x+room.w; x++) roomCells.set(cellKey(base,x,y),room);
  }
  const cells = new Set(), placed = new Map();
  // old kinds last (UPGRADE): an upgraded desk takes a tile more, which must not push out an object stored next to it
  const order = raw.objects.map((o, i) => [o, i]).sort(([a], [b]) => (UPGRADE[a?.kind] ? 1 : 0) - (UPGRADE[b?.kind] ? 1 : 0));
  // and where each old one stands now stays its own: an upgrade may not take it from the next one (who may need it to stay as it was)
  const reserved = new Map();
  for (const o of raw.objects) { const p = UPGRADE[o?.kind] && PRO.find((p) => p.id === o.kind);
    if (p && int(o.x) && int(o.y)) for (let y = o.y; y < o.y+p.h; y++) for (let x = o.x; x < o.x+p.w; x++) if (inReach(base,x,y)) reserved.set(cellKey(base,x,y), o); }
  for (const [o0, at] of order) {
    const place = (o, strict = !restore) => { // strict: a new save's rules (an upgrade must pass them too, or the next save would fail)
      const p = PRO.find((p) => p.id === o?.kind);
      if (!p || !int(o.x) || !int(o.y) || typeof o.id !== 'string' || !/^[a-zA-Z0-9-]{1,48}$/.test(o.id) || ids.has(o.id)) fail('E_OBJECT', 'malformed object');
      for (let y = o.y; y < o.y+p.h; y++) for (let x = o.x; x < o.x+p.w; x++) {
        if (protectedTile(base,x,y)) fail('E_OBJECT_FIXED', 'an object over a building or facility');
        const key = cellKey(base,x,y); if (cells.has(key)) fail('E_OBJECT_OVERLAP', 'objects overlap');
        const room = roomCells.get(key);
        const solid = room ? roomWall(room,x,y) ? '1' : terrainAt.get(key)?.solid ?? '0' : terrainAt.get(key)?.solid ?? baseSolid(base, x, y);
        if (solid !== '0') fail('E_OBJECT_GROUND', 'an object off free ground');
      }
      for (const r of rooms) for (const x of doorXs(r)) {
        // Also reserve the threshold and inner approach on new saves; old valid layouts still restore.
        for (let y = r.y+r.h-(strict ? 2 : 0); y <= r.y+r.h; y++)
          if (inside({...p,x:o.x,y:o.y},x,y)) fail('E_OBJECT_DOOR', 'an object in front of a room door');
      }
      return { id:o.id, kind:o.kind, x:o.x, y:o.y };
    };
    // an old kind: the first of its upgrades that fits, else as it was
    const ups = UPGRADE[o0?.kind]?.(o0) ?? [];
    let object = null;
    const mine = (u) => { const p = PRO.find((p) => p.id === u.kind); if (!p) return false;
      for (let y = u.y; y < u.y+p.h; y++) for (let x = u.x; x < u.x+p.w; x++) { const r = reserved.get(cellKey(base,x,y)); if (r && r !== o0) return false; }
      return true; };
    for (const u of ups) { if (!mine(u)) continue; try { object = place(u, true); onUpgrade(); break; } catch {} }
    object ??= recover(() => place(o0));
    if (!object) continue;
    ids.add(object.id); placed.set(at, object);
    const p = PRO.find(p => p.id === object.kind);
    for (let y = object.y; y < object.y+p.h; y++) for (let x = object.x; x < object.x+p.w; x++) cells.add(cellKey(base,x,y));
  }
  const objects = [...placed.keys()].sort((a, b) => a - b).map((i) => placed.get(i)); // in the order they came
  return { schema: MAP_SCHEMA, revision: raw.revision, tiles, objects, rooms, removals };
}
/** The open sea's tile at (x, y) past the base map's edge: the base map's sea repeats every SEA_PERIOD tiles
 *  (assets/map/map-art.mjs: the swell 12 tiles, the crests 6), so at each place in the period, the tile its sea cells
 *  there have most often (open water; foam by a shore, a wake or a buoy are rare). deck/tilemap.js createOcean draws
 *  the same period under the map, so a grown map's sea and the ocean round it line up. */
export const SEA_PERIOD = 12;
const seaCache = new WeakMap();
export function seaTiles(base) {
  let v = seaCache.get(base); if (v) return v;
  const P = SEA_PERIOD, W = base.width, counts = Array.from({ length: P * P }, () => new Map()), all = new Map();
  for (let y = 0; y < base.height; y++) for (let x = 0; x < W; x++) if (base.water[y][x] === '1') {
    const gid = base.art.data[y * W + x], m = counts[(y % P) * P + (x % P)];
    m.set(gid, (m.get(gid) ?? 0) + 1); all.set(gid, (all.get(gid) ?? 0) + 1);
  }
  const top = (m) => [...m].sort((a, b) => b[1] - a[1])[0]?.[0];
  const any = top(all) ?? base.art.data[0];
  v = counts.map((m) => top(m) ?? any);
  seaCache.set(base, v);
  return v;
}
const seaAt = (base, x, y) => seaTiles(base)[(((y % SEA_PERIOD) + SEA_PERIOD) % SEA_PERIOD) * SEA_PERIOD + (((x % SEA_PERIOD) + SEA_PERIOD) % SEA_PERIOD)];
/** Base + overlay, without changing either. Objects inherit real seats/voice circles and collision, not just art.
 *  Built past the base map's edge (GROW), the map grows to take it in: its grids then start at (x0, y0) (0 or less,
 *  in tiles; grid row y - y0, column x - x0 is cell (x, y)) and are width × height; the new cells are open sea. Every
 *  reader of the grids takes x0 / y0 into account (a base map has neither: 0). Everything else stays in the world's
 *  tiles (zones, seats, fronts, people), so nothing moves when the map grows. */
/** A placed prop's seats' name: its Chinese one, and the others for the page (map-names.js; none: no name, its kind's). */
const seatName = (id) => { const n = PROP_SEAT_NAMES[id]; return n ? { name: n['zh-CN'], names: { ...n } } : { name: '', names: undefined }; };
export function applyOverlay(base, overlay) {
  const m = { ...base, zones:[...(base.zones??[])], art: { ...base.art }, seats: [...(base.seats ?? [])],
    fronts: base.fronts ? { ...base.fronts, list: [...base.fronts.list] } : null };
  const BW = base.width, T = base.tile, TER = terrainsOf(base), PRO = propsOf(base);
  // how far the edits reach
  let X0 = 0, Y0 = 0, X1 = BW, Y1 = base.height;
  const reach = (x, y, w = 1, h = 1) => { X0 = Math.min(X0, x); Y0 = Math.min(Y0, y); X1 = Math.max(X1, x + w); Y1 = Math.max(Y1, y + h); };
  for (const t of overlay.tiles) reach(t.x, t.y);
  for (const r of overlay.rooms ?? []) reach(r.x, r.y, r.w, r.h);
  for (const o of overlay.objects) { const p = PRO.find((p) => p.id === o.kind); if (p) reach(o.x, o.y, p.w, p.h); }
  const W = X1 - X0, H = Y1 - Y0, at = (x, y) => (y - Y0) * W + x - X0;
  const art = new Array(W * H), water = [], solid = [];
  for (let y = Y0; y < Y1; y++) {
    const wr = [], sr = [];
    for (let x = X0; x < X1; x++) {
      if (inBase(base, x, y)) { art[at(x, y)] = base.art.data[y * BW + x]; wr.push(base.water[y][x]); sr.push(base.solid[y][x]); }
      else { art[at(x, y)] = seaAt(base, x, y); wr.push('1'); sr.push('1'); }
    }
    water.push(wr); solid.push(sr);
  }
  const put = (x, y, gid, w, s) => { art[at(x, y)] = gid; water[y - Y0][x - X0] = w; solid[y - Y0][x - X0] = s; };
  // built-in things taken away: not drawn, and the ground next to them where they stood (tiles below may paint it)
  if (overlay.removals?.length) {
    const OBJ = baseObjects(base), gone = new Set(overlay.removals.map((id) => OBJ.byId.get(id)?.front).filter(Boolean));
    if (m.fronts) m.fronts.list = m.fronts.list.filter((f) => !gone.has(f));
    for (const id of overlay.removals) for (const k of OBJ.byId.get(id)?.cells ?? []) if (OBJ.fill.has(k)) art[at(k % BW, Math.floor(k / BW))] = OBJ.fill.get(k);
  }
  for (const t of overlay.tiles) {
    const p = TER.find((p) => p.id === t.kind);
    put(t.x, t.y, base.art.data[p.y * BW + p.x], p.water, p.solid);
  }
  const edited=new Set(overlay.tiles.map(t=>at(t.x,t.y)));
  for(const r of overlay.rooms??[]){
    for(let y=r.y;y<r.y+r.h;y++)for(let x=r.x;x<r.x+r.w;x++){
      if(!roomWall(r,x,y)&&x>r.x&&x<r.x+r.w-1&&y>r.y&&y<r.y+r.h-1&&edited.has(at(x,y)))continue;
      const p=TER.find(p=>p.id===(roomWall(r,x,y)?'wall':'deck'));
      put(x,y,base.art.data[p.y*BW+p.x],p.water,p.solid);
    }
    // names: the English name its editor gave it, or the default name's when it has that (deck/places.js shows the reader's)
    const def = ROOM_DEFAULT_NAMES[r.kind], names = r.nameEn ? { en: r.nameEn } : r.name === def?.['zh-CN'] ? { ...def } : null;
    m.zones.push({id:'room-'+r.id,name:r.name,x:r.x+1,y:r.y+1,w:r.w-2,h:r.h-2,meeting:r.kind==='meeting',...(names ? { names } : {})});
  }
  for (const o of overlay.objects) {
    const p = PRO.find((p) => p.id === o.kind), dx = o.x - p.x, dy = o.y - p.y;
    for (let y = 0; y < p.h; y++) for (let x = 0; x < p.w; x++)
      put(o.x + x, o.y + y, base.art.data[(p.y + y) * BW + p.x + x], base.water[p.y + y][p.x + x], base.solid[p.y + y][p.x + x]);
    for (const [i, s] of (base.seats ?? []).entries()) if (inside(p, s.fx, s.fy)) m.seats.push({ ...s, ...(p.legacy ? { narrow: true } : {}),
      id: `edit-${o.id}-${i}`, group: s.group ? `edit-${o.id}-${s.group}` : null, ...seatName(p.id), // (agents: deck sit 会议圆桌)
      fx: s.fx + dx, fy: s.fy + dy, x: s.x + dx, y: s.y + dy });
    for (const f of base.fronts?.list ?? []) if (f.x >= p.x * T && f.y >= p.y * T && f.x + f.w <= (p.x + p.w) * T && f.y + f.h <= (p.y + p.h) * T)
      m.fronts?.list.push({ ...f, x: f.x + dx * T, y: f.y + dy * T, base: f.base + dy * T });
  }
  m.art.data = art; m.x0 = X0; m.y0 = Y0; m.width = W; m.height = H;
  m.water = water.map((r) => r.join('')); m.solid = solid.map((r) => r.join(''));
  return m;
}
/** Nearest spot that fits the full client body, also legal for server point collisions. Never respawn in a wall. */
export function safePosition(map, peer) {
  const c = collider(map); if (c.boxFree(peer.x, peer.y, !!peer.boat)) return null;
  let best = null, distance = Infinity;
  const x0 = map.x0 ?? 0, y0 = map.y0 ?? 0;
  for (let y = y0; y < y0 + map.height; y++) for (let x = x0; x < x0 + map.width; x++) {
    const px = x, py = y - .3, d = (px - peer.x) ** 2 + (py - peer.y) ** 2;
    if (d < distance && c.boxFree(px, py, !!peer.boat)) { best = { x: px, y: py, boat: !!peer.boat }; distance = d; }
  }
  // An administrator can remove every water cell while a boat is in it: bring its occupant safely ashore.
  return best ?? { x: map.spawn.x, y: map.spawn.y, boat: false };
}
